import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Swal from 'sweetalert2';
import SalesLayout from '../../components/SalesLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const INTRO = 'Convert every student inquiry into a clear follow-up plan that keeps momentum until registration.';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'in followup', label: 'In Followup' },
    { value: 'will not register', label: 'Will Not Register' },
    { value: 'registered', label: 'Registered' },
    { value: 'registered as inter branch', label: 'Registered as Inter Branch' },
    { value: 'registered in another institution', label: 'Registered in Another Institution' },
    { value: 'plan to register', label: 'Plan To Register' },
];

const formatStatusLabel = (value) => {
    if (!value) {
        return 'Status unknown';
    }

    return value
        .replace(/_/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0].toUpperCase() + part.slice(1))
        .join(' ');
};

const normalizeStatusKey = (value) => {
    if (!value && value !== 0) return '';
    return String(value).trim().toLowerCase();
};

const trimOrNull = (value) => {
    const trimmed = (value ?? '').trim();
    return trimmed ? trimmed : null;
};

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '28px',
        height: '28px',
        borderColor: state.isFocused ? '#0ea5e9' : '#cbd5f5',
        boxShadow: 'none',
        fontSize: '11px',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0 8px',
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '28px',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '4px',
    }),
    menu: (provided) => ({
        ...provided,
        fontSize: '11px',
    }),
    menuPortal: (provided) => ({
        ...provided,
        // Ensure dropdown renders above modal overlay
        zIndex: 1000,
    }),
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—';
    }

    const amount = Math.round(value);
    return `Rs ${new Intl.NumberFormat('en-US').format(amount)}`;
};

const resolveChecklistProgress = (row) => {
    if (!row || CHECKLIST_TOTAL === 0) {
        return { completed: 0, percentage: 0 };
    }

    const completed = CHECKLIST_FIELDS.reduce(
        (sum, field) => (row[field] ? sum + 1 : sum),
        0,
    );

    const percentageRaw = Math.round((completed / CHECKLIST_TOTAL) * 100);
    const percentage = Math.min(100, Math.max(0, percentageRaw));

    return {
        completed,
        percentage,
    };
};

const CHECKLIST_ITEMS = [
    {
        id: 'photo-uploaded',
        field: 'isPhotoUploaded',
        label: 'Upload student photo',
        description: 'Capture or upload the student photo before proceeding.',
    },
    {
        id: 'name-verified',
        field: 'isNameVerified',
        label: 'Verify student name',
        description: 'Confirm the full name against valid identification.',
    },
    {
        id: 'payment-agreement',
        field: 'isPaymentAgreementGiven',
        label: 'Collect payment agreement',
        description: 'Ensure the payment agreement form is signed and stored.',
    },
    {
        id: 'student-declaration',
        field: 'isStudentDeclarationGiven',
        label: 'Collect student declaration',
        description: 'File the student declaration acknowledgment for records.',
    },
    {
        id: 'whatsapp-group',
        field: 'isStudentAddedInWhatsAppGroup',
        label: 'Add to WhatsApp group',
        description: 'Add the student to the relevant course WhatsApp group.',
    },
];

const CHECKLIST_FIELD_BY_TASK_ID = CHECKLIST_ITEMS.reduce((accumulator, item) => {
    accumulator[item.id] = item.field;
    return accumulator;
}, {});

const CHECKLIST_FIELDS = CHECKLIST_ITEMS.map((item) => item.field);
const CHECKLIST_TOTAL = CHECKLIST_FIELDS.length;

const formatDisplayDate = (value) => {
    if (!value) {
        return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(parsed);
};

const formatDate = (value) => {
    if (!value) {
        return 'Not set';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.valueOf())) {
        return value;
    }

    return parsed.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
};

const resolveCourseLabel = (row) => {
    const explicit = trimOrNull(row.course);
    if (explicit) {
        return explicit;
    }

    if (typeof row.courseId === 'number' && !Number.isNaN(row.courseId)) {
        return `Course #${row.courseId}`;
    }

    return 'This course';
};

const resolveAssignedStaffInfo = (row) => {
    const staffId = row.createdByStaff?.id ?? null;
    const staffName =
        trimOrNull(row.createdByStaff?.name)
        ?? trimOrNull(row.createdBy)
        ?? (staffId ? `Staff #${staffId}` : null);

    return {
        name: staffName,
        id: staffId,
    };
};

const buildConflictDetails = (row) => {
    const student = trimOrNull(row.studentName) ?? trimOrNull(row.studentId) ?? 'Student';
    const courseLabel = resolveCourseLabel(row);
    const assignedStaff = resolveAssignedStaffInfo(row);
    const createdOnLabel = row.createdDate ? formatDate(row.createdDate) : 'Unknown date';
    const nicLabel = trimOrNull(row.nic) ?? 'NIC not recorded';

    return {
        student,
        courseLabel,
        assignedStaffName: assignedStaff.name,
        assignedStaffId: assignedStaff.id,
        createdOnLabel,
        nicLabel,
    };
};

const getStatusStyles = (statusKey) => {
    switch (statusKey) {
    case 'registered':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    case 'in followup':
    case 'plan to register':
        return 'bg-amber-100 text-amber-700 border border-amber-200';
    default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
};

const applyFieldUpdate = (row, field, rawValue, metadata) => {
    const value = rawValue ?? '';
    const trimmed = typeof value === 'string' ? value.trim() : String(value).trim();
    const updated = { ...row };

    if (field === 'statusKey') {
        const normalized = normalizeStatusKey(trimmed) || row.statusKey;
        updated.statusKey = normalized;
        updated.statusLabel = formatStatusLabel(normalized);
        return updated;
    }

    if (field === 'agreedAmount') {
        if (trimmed === '') {
            updated.agreedAmount = null;
            return updated;
        }

        const parsed = Number(trimmed);
        if (Number.isNaN(parsed)) {
            return row;
        }

        updated.agreedAmount = parsed;
        return updated;
    }

    if (field === 'registerDate') {
        const normalizedDate = trimmed || null;
        updated.registerDate = normalizedDate;
        updated.confirmedDateToRegister = normalizedDate;
        return updated;
    }

    if (field === 'course') {
        const courseOption = metadata?.courseOption ?? null;

        if (courseOption) {
            const parsedId = Number.parseInt(courseOption.value, 10);
            updated.courseId = Number.isNaN(parsedId) ? null : parsedId;
            updated.course = courseOption.label;
            return updated;
        }

        if (trimmed === '') {
            updated.courseId = null;
            updated.course = null;
            return updated;
        }

        const numericId = /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : null;
        updated.courseId = numericId;
        updated.course = trimmed;
        return updated;
    }

    const targetField = field;
    updated[targetField] = trimmed || null;

    return updated;
};

const getFieldValue = (row, field) => {
    switch (field) {
    case 'studentId':
        return row.studentId ?? null;
    case 'studentName':
        return row.studentName ?? null;
    case 'nic':
        return row.nic ?? null;
    case 'contact':
        return row.contact ?? null;
    case 'course':
        return row.courseId ?? row.course ?? null;
    case 'inquiryType':
        return row.inquiryType ?? null;
    case 'note':
        return row.note ?? null;
    case 'statusKey':
        return row.statusKey;
    case 'agreedAmount':
        return row.agreedAmount ?? null;
    case 'registerDate':
        return row.registerDate ?? null;
    default:
        return null;
    }
};

const normalizeComparableValue = (value) => {
    if (value === null || typeof value === 'undefined') {
        return '';
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    return value;
};

const serializePayloadValue = (value) => {
    if (value === null || typeof value === 'undefined') {
        return '';
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    return value;
};

const buildEditFormState = (row, courseOptions) => {
    const normalizeString = (value) => (value ?? '').toString();

    const courseId = row.courseId !== null && row.courseId !== undefined ? String(row.courseId) : '';
    const matchedCourse = courseId
        ? courseOptions.find((option) => String(option.value) === courseId) ?? null
        : null;
    const fallbackCourse = !matchedCourse && row.course
        ? {
              value: `label:${row.course}`,
              label: normalizeString(row.course),
              shortName: null,
              fullName: null,
              searchValue: normalizeString(row.course).toLowerCase(),
          }
        : null;
    const courseOption = matchedCourse ?? fallbackCourse;
    const courseLabel = courseOption?.label ?? '';

    return {
        studentId: normalizeString(row.studentId ?? ''),
        studentName: normalizeString(row.studentName ?? ''),
        nic: normalizeString(row.nic ?? ''),
        contactNumber1: normalizeString(row.contactNumber1 ?? ''),
        contactNumber2: normalizeString(row.contactNumber2 ?? ''),
        contactNumber3: normalizeString(row.contactNumber3 ?? ''),
        courseOption,
        courseId,
        courseLabel,
        inquiryType: normalizeString(row.inquiryType ?? ''),
        note: normalizeString(row.note ?? ''),
        statusKey: row.statusKey ?? 'pending',
        agreedAmount: row.agreedAmount !== null && row.agreedAmount !== undefined ? String(row.agreedAmount) : '',
        registerDate: row.registerDate ?? row.confirmedDateToRegister ?? '',
    };
};

    const derivePrimaryContact = (form) => {
    const contacts = [form.contactNumber1, form.contactNumber2, form.contactNumber3].map((value) =>
        typeof value === 'string' ? value.trim() : String(value ?? '').trim(),
    );
    const found = contacts.find((value) => value.length > 0);
    return found && found.length > 0 ? found : null;
};

const parseNumericId = (value) => {
    if (value === null || typeof value === 'undefined') {
        return null;
    }
    const v = String(value).trim();
    return /^\d+$/.test(v) ? Number.parseInt(v, 10) : null;
};

const resolveCourseIdForSubmit = (form, options) => {
    const byText = parseNumericId(form.courseId);
    if (byText !== null) return byText;

    const byOption = parseNumericId(form.courseOption?.value ?? null);
    if (byOption !== null) return byOption;

    const label = (form.courseLabel ?? '').trim().toLowerCase();
    if (!label) return null;

    const matched = options.find((opt) => {
        const haystack = [opt.label, opt.shortName ?? '', opt.fullName ?? '', opt.searchValue ?? '']
            .filter(Boolean)
            .map((v) => v.toLowerCase());
        return haystack.some((h) => h.includes(label) || label.includes(h));
    });

    return matched ? parseNumericId(matched.value) : null;
};

export default function Inquiry({ pageTitle, section }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [courseOptions, setCourseOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCourseId, setFilterCourseId] = useState('');
    const [editingCell, setEditingCell] = useState(null);
    const selectMenuPortalTarget = typeof window === 'undefined' ? undefined : document.body;
    const [editModal, setEditModal] = useState(() => ({
        isOpen: false,
        originalRow: null,
        form: {
            studentId: '',
            studentName: '',
            nic: '',
            contactNumber1: '',
            contactNumber2: '',
            contactNumber3: '',
            courseOption: null,
            courseId: '',
            courseLabel: '',
            inquiryType: '',
            note: '',
            statusKey: 'pending',
            agreedAmount: '',
            registerDate: '',
        },
        errors: {},
        submitting: false,
    }));

    const [followUpModal, setFollowUpModal] = useState({ isOpen: false, row: null });
    const [followUpHistory, setFollowUpHistory] = useState([]);
    const [followUpForm, setFollowUpForm] = useState({
        comment: '',
        nextReminderDate: '',
        nextReminderComment: '',
        submitting: false,
        error: '',
    });
    const csrfToken = '';
    const [checklistModal, setChecklistModal] = useState({ isOpen: false, row: null, tasks: [] });

    const escapeHtml = (value) => {
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        };
        return value.replace(/[&<>"']/g, (char) => map[char] ?? char);
    };

    const showConflictAlert = (details) => {
        const safeLabel = (value, fallback) => {
            const trimmed = typeof value === 'string' ? value.trim() : String(value ?? '').trim();
            return trimmed || fallback;
        };

        const studentDisplay = safeLabel(details.student, 'Student');
        const nicDisplay = safeLabel(details.nicLabel, 'NIC not recorded');
        const courseDisplay = safeLabel(details.courseLabel, 'this course');
        const staffNameBase = safeLabel(details.assignedStaffName, 'Unknown staff');
        const staffDisplay = details.assignedStaffId ? `${staffNameBase} (ID: ${details.assignedStaffId})` : staffNameBase;
        const createdDisplay = safeLabel(details.createdOnLabel, 'Date not recorded');
        const resolution = safeLabel(details.resolutionMessage, 'Coordinate with the assigned staff before proceeding.');

        Swal.fire({
            icon: 'warning',
            iconColor: '#f59e0b',
            title: '<strong>Duplicate Inquiry Detected</strong>',
            html: `
                <div style="text-align: center; padding: 10px 0;">
                    <p style="font-size: 15px; margin-bottom: 20px; color: #374151;">
                        An existing inquiry for <strong style="color: #0ea5e9;">${escapeHtml(studentDisplay)}</strong> 
                        on <strong style="color: #0ea5e9;">${escapeHtml(courseDisplay)}</strong> already exists.
                    </p>
                    <div style="background: #f8fafc; border-left: 4px solid #f59e0b; padding: 15px; margin: 15px 0; text-align: left; border-radius: 4px;">
                        <p style="margin: 8px 0; color: #1e293b; font-size: 14px;">
                            <strong style="color: #64748b;">📋 Currently followed by:</strong> 
                            <span style="color: #0ea5e9; font-weight: 600;">${escapeHtml(staffDisplay)}</span>
                        </p>
                        <p style="margin: 8px 0; color: #1e293b; font-size: 14px;">
                            <strong style="color: #64748b;">🆔 NIC:</strong> 
                            <span style="color: #475569; font-weight: 500;">${escapeHtml(nicDisplay)}</span>
                        </p>
                        <p style="margin: 8px 0; color: #1e293b; font-size: 14px;">
                            <strong style="color: #64748b;">📅 Created on:</strong> 
                            <span style="color: #475569; font-weight: 500;">${escapeHtml(createdDisplay)}</span>
                        </p>
                    </div>
                    <p style="margin-top: 20px; color: #dc2626; font-size: 14px; font-weight: 500;">
                        ⚠️ ${escapeHtml(resolution)}
                    </p>
                </div>
            `,
            confirmButtonText: 'Got it',
            confirmButtonColor: '#0ea5e9',
            width: '550px',
            customClass: {
                popup: 'swal2-inquiry-conflict',
                title: 'swal2-title-custom',
            },
        });
    };

    const normalizePhone = (value) => {
        const v = (value ?? '').replace(/\D+/g, '');
        return v.length >= 7 ? v : '';
    };

    const findDuplicateInquiries = (payload) => {
        const nic = (payload.nic ?? '').trim().toLowerCase();
        const contacts = (payload.contacts ?? []).map((c) => normalizePhone(c));
        const courseLabel = (payload.courseLabel ?? '').trim().toLowerCase();
        const courseId = payload.courseId ?? null;
        const excludeId = payload.excludeId ?? null;

        const match = tableRows.find((row) => {
            if (excludeId && row.id === excludeId) return false;

            const rowCourseLabel = (row.course ?? '').trim().toLowerCase();
            let sameCourse = false;

            if (courseLabel) {
                // Compare by course name when provided so duplicated course IDs
                // with the same label are treated as the same course.
                sameCourse = rowCourseLabel && rowCourseLabel === courseLabel;
            } else {
                // Fallback to ID comparison if no label was supplied.
                sameCourse = (row.courseId ?? null) === courseId;
            }

            if (!sameCourse) return false;
            const rowNic = (row.nic ?? '').trim().toLowerCase();
            const nicMatches = nic && rowNic && rowNic === nic;
            const rowContacts = [row.contactNumber1, row.contactNumber2, row.contactNumber3, row.contact]
                .filter(Boolean)
                .map((c) => normalizePhone(c));
            const contactMatches = contacts.length > 0 && rowContacts.some((rc) => rc && contacts.includes(rc));
            return nicMatches || contactMatches;
        });

        return match ?? null;
    };

    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/admin/sales-inquiries');
                if (!response?.data || cancelled) return;

                const data = response.data;
                setTableRows(data.inquiries ?? []);
                setCourseOptions(data.courseOptions ?? []);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load inquiries:', error);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            cancelled = true;
        };
    }, []);

    const courseSelectOptions = useMemo(() => {
        return courseOptions.map((option) => {
            const shortName = option.shortName ?? null;
            const fullName = option.fullName ?? null;
            const derivedLabel = option.label || [shortName, fullName].filter(Boolean).join('  ');

            return {
                value: option.value,
                label: derivedLabel,
                shortName,
                fullName,
                searchValue: [derivedLabel, shortName, fullName]
                    .filter((part) => Boolean(part))
                    .map((part) => part.toLowerCase())
                    .join(' '),
            };
        });
    }, [courseOptions]);

    // De-duplicated options for the course filter dropdown: one entry per label,
    // but remember all underlying courseIds so filtering can match every row.
    const courseFilterOptions = useMemo(() => {
        const map = new Map();
        courseSelectOptions.forEach((option) => {
            const key = option.label.toLowerCase();
            if (!map.has(key)) {
                map.set(key, {
                    value: key,
                    label: option.label,
                    shortName: option.shortName,
                    fullName: option.fullName,
                    searchValue: option.searchValue,
                    courseIds: [String(option.value)],
                });
            } else {
                const existing = map.get(key);
                if (!existing.courseIds.includes(String(option.value))) {
                    existing.courseIds.push(String(option.value));
                }
            }
        });
        return Array.from(map.values());
    }, [courseSelectOptions]);

    const resetEditModal = () => {
        setEditModal({
            isOpen: false,
            originalRow: null,
            form: {
                studentId: '',
                studentName: '',
                nic: '',
                contactNumber1: '',
                contactNumber2: '',
                contactNumber3: '',
                courseOption: null,
                courseId: '',
                courseLabel: '',
                inquiryType: '',
                note: '',
                statusKey: 'pending',
                agreedAmount: '',
                registerDate: '',
            },
            errors: {},
            submitting: false,
        });
    };

    const openEditModal = (row) => {
        setEditModal({
            isOpen: true,
            originalRow: row,
            form: buildEditFormState(row, courseSelectOptions),
            errors: {},
            submitting: false,
        });
    };

    const closeEditModal = () => {
        resetEditModal();
    };

    const openCreateModal = () => {
        setEditModal({
            isOpen: true,
            originalRow: null,
            form: {
                studentId: '',
                studentName: '',
                nic: '',
                contactNumber1: '',
                contactNumber2: '',
                contactNumber3: '',
                courseOption: null,
                courseId: '',
                courseLabel: '',
                inquiryType: '',
                note: '',
                statusKey: 'pending',
                agreedAmount: '',
                registerDate: '',
            },
            errors: {},
            submitting: false,
        });
    };

    const openFollowUpModal = (row) => {
        setFollowUpModal({ isOpen: true, row });
        // Load history from API
        if (row?.id) {
            axiosInstance
                .get(`/admin/sales-inquiries/${row.id}/followups`)
                .then((res) => {
                    const items = res.data?.data ?? [];
                    setFollowUpHistory(items);
                })
                .catch(() => setFollowUpHistory([]));
        }
    };

    const closeFollowUpModal = () => {
        setFollowUpModal({ isOpen: false, row: null });
        setFollowUpHistory([]);
        setFollowUpForm({ comment: '', nextReminderDate: '', nextReminderComment: '', submitting: false, error: '' });
    };

    const submitFollowUp = () => {
        if (!followUpModal.row) return;
        const payload = {
            comment: followUpForm.comment.trim(),
            next_reminder_date: followUpForm.nextReminderDate.trim() || null,
            next_reminder_comment: followUpForm.nextReminderComment.trim() || null,
        };
        if (!payload.comment) {
            setFollowUpForm((p) => ({ ...p, error: 'Comment is required.' }));
            return;
        }
        setFollowUpForm((p) => ({ ...p, submitting: true, error: '' }));
        axiosInstance
            .post(`/admin/sales-inquiries/${followUpModal.row.id}/followups`, payload)
            .then(() => {
                // Reload history
                return axiosInstance.get(`/admin/sales-inquiries/${followUpModal.row.id}/followups`);
            })
            .then((res) => {
                const items = res.data?.data ?? [];
                setFollowUpHistory(items);
                setFollowUpForm({ comment: '', nextReminderDate: '', nextReminderComment: '', submitting: false, error: '' });
            })
            .catch((err) => {
                setFollowUpForm((p) => ({
                    ...p,
                    submitting: false,
                    error: err?.response?.data?.message || err.message || 'Unable to save follow-up.',
                }));
            });
    };

    const buildChecklistTasks = (row) =>
        CHECKLIST_ITEMS.map((item) => ({
            id: item.id,
            label: item.label,
            description: item.description,
            field: item.field,
            completed: Boolean(row?.[item.field] ?? false),
        }));

    const openChecklistModal = (row) => {
        setChecklistModal({
            isOpen: true,
            row,
            tasks: buildChecklistTasks(row),
        });
    };

    const closeChecklistModal = () => {
        setChecklistModal({ isOpen: false, row: null, tasks: [] });
    };

    const toggleChecklistTask = (taskId) => {
        const field = CHECKLIST_FIELD_BY_TASK_ID[taskId];
        const row = checklistModal.row;

        if (!field || !row) {
            return;
        }

        const rowId = row.id;
        const nextValue = !Boolean(row[field]);

        setChecklistModal((previous) => ({
            ...previous,
            row: previous.row && previous.row.id === rowId ? { ...previous.row, [field]: nextValue } : previous.row,
            tasks: previous.tasks.map((task) =>
                task.id === taskId ? { ...task, completed: nextValue } : task,
            ),
        }));

        setTableRows((rows) =>
            rows.map((current) => (current.id === rowId ? { ...current, [field]: nextValue } : current)),
        );

        const payload = { field, value: nextValue ? '1' : '0' };

        axiosInstance
            .put(`/admin/sales-inquiries/${rowId}`, payload)
            .then(() => {
                // already optimistically updated state; show a small success toast
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Checklist updated',
                    showConfirmButton: false,
                    timer: 1600,
                    timerProgressBar: true,
                });
            })
            .catch(() => {
            setChecklistModal((previous) => ({
                ...previous,
                row:
                    previous.row && previous.row.id === rowId
                        ? { ...previous.row, [field]: !nextValue }
                        : previous.row,
                tasks: previous.tasks.map((task) =>
                    task.id === taskId ? { ...task, completed: !nextValue } : task,
                ),
            }));

            setTableRows((rows) =>
                rows.map((current) => (current.id === rowId ? { ...current, [field]: !nextValue } : current)),
            );

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Unable to update checklist',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });
        });
    };

    const updateEditForm = (field, value) => {
        setEditModal((prev) => {
            const { [field]: _ignored, ...restErrors } = prev.errors || {};
            return {
                ...prev,
                form: {
                    ...prev.form,
                    [field]: value,
                },
                errors: restErrors,
            };
        });
    };

    const updateCourseSelection = (option) => {
        const value = option ? String(option.value ?? '') : '';
        const label = option?.label ?? '';

        setEditModal((prev) => {
            const { courseId: _removed, ...rest } = prev.errors || {};
            return {
                ...prev,
                form: {
                    ...prev.form,
                    courseOption: option,
                    courseId: value,
                    courseLabel: label,
                },
                errors: rest,
            };
        });
    };

    const applyModalUpdatesToRow = (row, form, options) => {
        const statusKey = form.statusKey || row.statusKey;
        const agreedAmountValue = form.agreedAmount.trim();
        const registerDateValue = form.registerDate.trim();
        const resolvedCourseId = resolveCourseIdForSubmit(form, options);
        const courseOption = form.courseOption;
        const lookupLabel = resolvedCourseId !== null
            ? options.find((option) => String(option.value) === String(resolvedCourseId))?.label ?? ''
            : '';
        const courseLabel = (courseOption?.label || lookupLabel || form.courseLabel).trim();

        const normalizedRegisterDate = registerDateValue || null;

        return {
            ...row,
            studentId: form.studentId.trim() || null,
            studentName: form.studentName.trim() || null,
            nic: form.nic.trim() || null,
            contactNumber1: form.contactNumber1.trim() || null,
            contactNumber2: form.contactNumber2.trim() || null,
            contactNumber3: form.contactNumber3.trim() || null,
            contact: derivePrimaryContact(form),
            courseId: resolvedCourseId,
            course: courseLabel ? courseLabel : null,
            inquiryType: form.inquiryType.trim() || null,
            note: form.note.trim() || null,
            statusKey,
            statusLabel: formatStatusLabel(statusKey),
            agreedAmount: agreedAmountValue !== '' ? Number(agreedAmountValue) : null,
            registerDate: normalizedRegisterDate,
            confirmedDateToRegister: normalizedRegisterDate,
        };
    };

    const validateInquiryForm = (form, { resolvedCourseId, isCreate }) => {
        const errors = {};

        const studentName = (form.studentName || '').trim();
        const nic = (form.nic || '').trim();
        const statusKey = (form.statusKey || '').trim();
        const agreedAmountRaw = (form.agreedAmount || '').trim();
        const registerDate = (form.registerDate || '').trim();

        // Require a student name for all records
        if (!studentName) {
            errors.studentName = 'Student name is required.';
        }

        // Require at least one contact number on create; for edit, keep the same rule
        const primaryContact = derivePrimaryContact(form);
        if (!primaryContact) {
            errors.contactNumber1 = 'At least one contact number is required.';
        }

        // Basic NIC sanity check (optional field, but if given it should not be too short)
        if (nic && nic.length < 5) {
            errors.nic = 'NIC looks too short.';
        }

        // Course must be specified so the inquiry can be tracked properly
        if (!resolvedCourseId) {
            errors.courseId = 'Course is required.';
        }

        // Status is required (UI already shows this as mandatory)
        if (!statusKey) {
            errors.statusKey = 'Status is required.';
        }

        // Agreed amount must be a valid, non-negative number when provided
        if (agreedAmountRaw !== '') {
            const amount = Number(agreedAmountRaw);
            if (Number.isNaN(amount)) {
                errors.agreedAmount = 'Agreed amount must be a number.';
            } else if (amount < 0) {
                errors.agreedAmount = 'Agreed amount cannot be negative.';
            }
        }

        // When the inquiry is considered registered / will register, require a date
        const statusNeedingDate = [
            'registered',
            'registered as inter branch',
            'registered in another institution',
            'plan to register',
        ];
        if (statusNeedingDate.includes(statusKey.toLowerCase()) && !registerDate) {
            errors.registerDate = 'Register date is required for this status.';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors,
        };
    };

    const submitEditModal = () => {
        const form = editModal.form;
        const isCreate = !editModal.originalRow;
        const resolvedCourseId = resolveCourseIdForSubmit(form, courseSelectOptions);

        // Front-end validation before any duplicate checks or API calls
        const { isValid, errors } = validateInquiryForm(form, { resolvedCourseId, isCreate });
        if (!isValid) {
            setEditModal((prev) => ({
                ...prev,
                errors,
            }));
            return;
        }

        // Duplicate safeguard (NIC/contact + same course name)
        const duplicate = findDuplicateInquiries({
            nic: form.nic,
            contacts: [form.contactNumber1, form.contactNumber2, form.contactNumber3],
            courseId: resolvedCourseId,
            courseLabel: form.courseLabel,
            excludeId: editModal.originalRow?.id ?? null,
        });
        if (duplicate) {
            const conflictDetails = buildConflictDetails(duplicate);
            showConflictAlert({
                ...conflictDetails,
                resolutionMessage: 'Please coordinate instead of creating or updating a duplicate record.',
            });
            return; // Block submit
        }
        const payload = {
            studentId: form.studentId.trim() || null,
            studentName: form.studentName.trim() || null,
            nic: form.nic.trim() || null,
            contactNumber1: form.contactNumber1.trim() || null,
            contactNumber2: form.contactNumber2.trim() || null,
            contactNumber3: form.contactNumber3.trim() || null,
            courseId: resolvedCourseId,
            courseLabel: form.courseLabel.trim() || null,
            inquiryType: form.inquiryType.trim() || null,
            note: form.note.trim() || null,
            statusKey: form.statusKey,
            agreedAmount: form.agreedAmount.trim() || null,
            registerDate: form.registerDate.trim() || null,
            confirmedDateToRegister: form.registerDate.trim() || null,
        };

        setEditModal((prev) => ({ ...prev, submitting: true, errors: {} }));
        if (isCreate) {
            axiosInstance
                .post('/admin/sales-inquiries', payload)
                .then((response) => {
                    const newRow = response?.data?.data ?? null;
                    if (newRow) {
                        setTableRows((rows) => [newRow, ...rows]);
                    }
                    resetEditModal();
                })
                .catch((error) => {
                    if (error?.response?.status === 409 && error.response.data?.duplicate) {
                        const duplicate = error.response.data.duplicate;
                        const conflictDetails = buildConflictDetails(duplicate);
                        showConflictAlert({
                            ...conflictDetails,
                            resolutionMessage:
                                'Please coordinate instead of creating a duplicate record.',
                        });
                        return;
                    }
                    const serverErrors = error?.response?.data?.errors || {};
                    const mappedErrors = {};
                    Object.entries(serverErrors).forEach(([key, value]) => {
                        mappedErrors[key] = Array.isArray(value)
                            ? value[0] || ''
                            : typeof value === 'string'
                                ? value
                                : '';
                    });
                    if (Object.keys(mappedErrors).length > 0) {
                        setEditModal((prev) => ({ ...prev, errors: mappedErrors }));
                    }
                })
                .finally(() => {
                    setEditModal((prev) => ({ ...prev, submitting: false }));
                });
            return;
        }
        axiosInstance
            .put(`/admin/sales-inquiries/${editModal.originalRow.id}`, payload)
            .then((response) => {
                const updated = response?.data?.data ?? null;
                if (updated) {
                    setTableRows((rows) =>
                        rows.map((row) => (row.id === updated.id ? updated : row)),
                    );
                }
                resetEditModal();
            })
            .catch((error) => {
                if (error?.response?.status === 409 && error.response.data?.duplicate) {
                    const duplicate = error.response.data.duplicate;
                    const conflictDetails = buildConflictDetails(duplicate);
                    showConflictAlert({
                        ...conflictDetails,
                        resolutionMessage:
                            'Edit declined to avoid conflict. Please coordinate with the assigned staff first.',
                    });
                    return;
                }
                const serverErrors = error?.response?.data?.errors || {};
                const mappedErrors = {};
                Object.entries(serverErrors).forEach(([key, value]) => {
                    mappedErrors[key] = Array.isArray(value)
                        ? value[0] || ''
                        : typeof value === 'string'
                            ? value
                            : '';
                });
                if (Object.keys(mappedErrors).length > 0) {
                    setEditModal((prev) => ({ ...prev, errors: mappedErrors }));
                }
            })
            .finally(() => {
                setEditModal((prev) => ({ ...prev, submitting: false }));
            });
    };

    const persistInquiryField = (field, updatedRow, previousRow, metadata) => {
        const previousComparable = normalizeComparableValue(getFieldValue(previousRow, field));
        const nextComparable = normalizeComparableValue(getFieldValue(updatedRow, field));

        if (previousComparable === nextComparable) {
            return;
        }

        const payloadValue =
            metadata?.payloadValue !== undefined
                ? metadata.payloadValue
                : serializePayloadValue(getFieldValue(updatedRow, field));

        const payload = {
            field,
            value: payloadValue,
        };

        axiosInstance
            .put(`/admin/sales-inquiries/${updatedRow.id}`, payload)
            .then((response) => {
                const serverRow = response?.data?.data ?? null;
                if (!serverRow) return;

                setTableRows((rows) => rows.map((row) => (row.id === serverRow.id ? serverRow : row)));

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Inquiry updated successfully',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            })
            .catch(() => {
                // On failure, revert the local optimistic change.
                setTableRows((rows) => rows.map((row) => (row.id === previousRow.id ? previousRow : row)));
            });
    };

    const inquiryTypeOptions = useMemo(() => {
        const map = new Map();

        tableRows.forEach((row) => {
            const type = row.inquiryType?.trim();
            if (!type) {
                return;
            }

            if (!map.has(type)) {
                map.set(type, { value: type, label: type });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [tableRows]);

    const beginEditing = (rowId, field) => {
        if (editingCell && (editingCell.rowId !== rowId || editingCell.field !== field)) {
            finalizeEdit(false);
        }

        const row = tableRows.find((item) => item.id === rowId);
        if (!row) {
            return;
        }

        let initialValue = '';

        if (field === 'agreedAmount') {
            initialValue = row.agreedAmount !== null && row.agreedAmount !== undefined ? String(row.agreedAmount) : '';
        } else if (field === 'statusKey') {
            initialValue = row.statusKey ?? '';
        } else if (field === 'course') {
            initialValue = row.courseId !== null && row.courseId !== undefined ? String(row.courseId) : '';
        } else {
            const rawValue = row[field];
            initialValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
        }

        setEditingCell({ rowId, field, value: initialValue });
    };

    const updateEditingValue = (value) => {
        setEditingCell((current) => (current ? { ...current, value } : current));
    };

    const finalizeEdit = (commit, overrideValue, metadata) => {
        if (!editingCell) {
            return;
        }

        if (!commit) {
            setEditingCell(null);
            return;
        }

        const { rowId, field, value } = editingCell;
        const nextValue = overrideValue !== undefined ? overrideValue : value;
        const existingRow = tableRows.find((item) => item.id === rowId);

        if (!existingRow) {
            setEditingCell(null);
            return;
        }

        const updatedRow = applyFieldUpdate(existingRow, field, nextValue, metadata);

        // Prevent inline edits that introduce duplicates (NIC/contact with same course name)
        if (field === 'nic' || field === 'contact' || field === 'course') {
            const dup = findDuplicateInquiries({
                nic: (field === 'nic' ? (nextValue ?? '') : existingRow.nic) ?? '',
                contacts:
                    field === 'contact'
                        ? [String(nextValue ?? '')]
                        : [existingRow.contactNumber1 ?? '', existingRow.contactNumber2 ?? '', existingRow.contactNumber3 ?? '', existingRow.contact ?? ''],
                courseId: field === 'course' ? (updatedRow.courseId ?? null) : (existingRow.courseId ?? null),
                courseLabel: field === 'course' ? (updatedRow.course ?? '') : (existingRow.course ?? ''),
                excludeId: existingRow.id,
            });
            if (dup) {
                const conflictDetails = buildConflictDetails(dup);
                showConflictAlert({
                    ...conflictDetails,
                    resolutionMessage: 'Edit declined to avoid conflict. Please coordinate with the assigned staff first.',
                });
                setEditingCell(null);
                return;
            }
        }
        const previousComparable = normalizeComparableValue(getFieldValue(existingRow, field));
        const nextComparable = normalizeComparableValue(getFieldValue(updatedRow, field));

        if (previousComparable === nextComparable) {
            setEditingCell(null);
            return;
        }

        setTableRows((prev) => prev.map((row) => (row.id === rowId ? updatedRow : row)));
        persistInquiryField(field, updatedRow, existingRow, metadata);
        setEditingCell(null);
    };

    const statusOptions = useMemo(() => {
        const map = new Map(STATUS_OPTIONS.map((option) => [option.value, option]));

        tableRows.forEach((row) => {
            const key = normalizeStatusKey(row.statusKey);

            if (!key || map.has(key)) {
                return;
            }

            map.set(key, { value: key, label: formatStatusLabel(key) });
        });

        return Array.from(map.values());
    }, [tableRows]);

    const enumFieldOptions = useMemo(
        () => ({
            statusKey: statusOptions,
            inquiryType: inquiryTypeOptions,
        }),
        [inquiryTypeOptions, statusOptions],
    );

    const stats = useMemo(() => {
        const total = tableRows.length;
        const registered = tableRows.filter((item) => item.statusKey === 'registered').length;
        const pipeline = total - registered;

        return {
            total,
            registered,
            pipeline,
            conversionRate: total > 0 ? Math.round((registered / total) * 100) : 0,
        };
    }, [tableRows]);

    const filteredInquiries = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const bySearch = (row) => {
            if (!query) return true;
            const haystack = [
                row.studentId,
                row.studentName,
                row.nic,
                row.contact,
                row.course,
                row.inquiryType,
                row.statusLabel,
                row.note,
            ]
                .filter(Boolean)
                .map((value) => value.toLowerCase());
            return haystack.some((value) => value.includes(query));
        };

        const byStatus = (row) => {
            if (!filterStatus) return true;
            return normalizeStatusKey(row.statusKey) === normalizeStatusKey(filterStatus);
        };

        const byCourse = (row) => {
            if (!filterCourseId) return true;
            const selected = courseFilterOptions.find((o) => String(o.value) === String(filterCourseId));
            if (!selected) return true;

            const selectedIds = (selected.courseIds || []).map((id) => String(id));

            const id = row.courseId !== null && row.courseId !== undefined ? String(row.courseId) : '';
            if (id && selectedIds.length > 0 && selectedIds.includes(id)) {
                return true;
            }

            // Fallback: match by label text when courseId is missing or not mapped
            const label = (row.course ?? '').toLowerCase();
            if (!label) return false;
            const parts = [selected.label, selected.shortName ?? '', selected.fullName ?? '']
                .filter(Boolean)
                .map((v) => v.toLowerCase());
            return parts.some((p) => p && label.includes(p));
        };

        return tableRows.filter((row) => bySearch(row) && byStatus(row) && byCourse(row));
    }, [tableRows, searchTerm, filterStatus, filterCourseId, courseFilterOptions]);

    const selectedCourseOption = useMemo(() => {
        const current = editModal.form.courseOption;
        if (current) {
            if (current.value && courseSelectOptions.length > 0) {
                const match = courseSelectOptions.find((option) => String(option.value) === String(current.value));
                return match ?? current;
            }
            return current;
        }

        const label = editModal.form.courseLabel.trim();
        if (label) {
            return {
                value: `label:${label}`,
                label,
                shortName: null,
                fullName: null,
                searchValue: label.toLowerCase(),
            };
        }

        const courseId = editModal.form.courseId.trim();
        if (courseId) {
            const match = courseSelectOptions.find((option) => String(option.value) === courseId);
            if (match) {
                return match;
            }
        }

        return null;
    }, [editModal.form.courseOption, editModal.form.courseLabel, editModal.form.courseId, courseSelectOptions]);

    const selectedInquiryTypeOption = useMemo(() => {
        const trimmed = editModal.form.inquiryType.trim();
        if (!trimmed) {
            return null;
        }

        return inquiryTypeOptions.find((option) => option.value === trimmed) ?? {
            value: trimmed,
            label: trimmed,
        };
    }, [editModal.form.inquiryType, inquiryTypeOptions]);

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            {loading ? (
                <PageLoader label="Loading inquiries…" />
            ) : (
            <div className="space-y-6">
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-slate-900">{pageTitle}</h1>
                            <p className="mt-2 text-sm text-slate-500">{INTRO}</p>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                            <div className="rounded-xl bg-slate-900/90 px-4 py-3 text-white shadow-sm">
                                <p className="text-xs uppercase tracking-wide text-white/70">Total inquiries</p>
                                <p className="mt-1 text-lg font-semibold">{stats.total}</p>
                            </div>
                            <div className="rounded-xl bg-emerald-500/90 px-4 py-3 text-white shadow-sm">
                                <p className="text-xs uppercase tracking-wide text-white/70">Registered</p>
                                <p className="mt-1 text-lg font-semibold">{stats.registered}</p>
                            </div>
                            <div className="rounded-xl bg-amber-500/90 px-4 py-3 text-white shadow-sm">
                                <p className="text-xs uppercase tracking-wide text-white/70">In pipeline</p>
                                <p className="mt-1 text-lg font-semibold">{stats.pipeline}</p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 shadow-sm">
                                <p className="text-xs uppercase tracking-wide text-slate-500">Conversion</p>
                                <p className="mt-1 text-lg font-semibold">{stats.conversionRate}%</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Student inquiries</h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Capture every conversation, update statuses quickly, and keep follow-ups aligned with your registration goals.
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <button
                                type="button"
                                onClick={() => navigate('/sales/performance')}
                                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                            >
                                My Performance
                            </button>
                            <button
                                type="button"
                                onClick={() => openCreateModal()}
                                className="inline-flex items-center justify-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                            >
                                Add Inquiry
                            </button>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative w-full sm:w-72">
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Search by student, NIC, or course..."
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            />
                        </div>
                        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                            <div className="sm:w-60">
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    options={statusOptions}
                                    value={filterStatus ? statusOptions.find((o) => o.value === filterStatus) ?? null : null}
                                    onChange={(option) => setFilterStatus(option?.value ?? '')}
                                    isClearable
                                    placeholder="Filter by status"
                                />
                            </div>
                            <div className="sm:w-[300px]">
                                <Select
                                    styles={selectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    options={courseFilterOptions}
                                    value={
                                        filterCourseId
                                            ? courseFilterOptions.find((o) => String(o.value) === String(filterCourseId)) ?? null
                                            : null
                                    }
                                    onChange={(option) => setFilterCourseId(option ? String(option.value) : '')}
                                    isClearable
                                    placeholder="Filter by course"
                                    getOptionLabel={(option) => option.shortName ?? option.label}
                                    formatOptionLabel={(option) => (
                                        <div className="flex flex-col">
                                            <span className="text-xs font-semibold text-slate-900">{option.shortName ?? option.label}</span>
                                            {option.fullName ? (
                                                <span className="text-[11px] text-slate-500">{option.fullName}</span>
                                            ) : null}
                                        </div>
                                    )}
                                    filterOption={(candidate, rawInput) => {
                                        const search = rawInput.trim().toLowerCase();
                                        if (!search) {
                                            return true;
                                        }
                                        const data = candidate.data || {};
                                        const haystack = [
                                            candidate.label,
                                            data?.shortName ?? '',
                                            data?.fullName ?? '',
                                            data?.searchValue ?? '',
                                        ]
                                            .filter(Boolean)
                                            .map((value) => value.toLowerCase());
                                        return haystack.some((value) => value.includes(search));
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                                <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="px-4 py-3">Student ID</th>
                                        <th className="px-4 py-3">Student Name</th>
                                        <th className="px-4 py-3">NIC</th>
                                        <th className="px-4 py-3">Contact</th>
                                        <th
                                            className="px-4 py-3"
                                            style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}
                                        >
                                            Course
                                        </th>
                                        <th
                                            className="px-4 py-3"
                                            style={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}
                                        >
                                            Inquiry Type
                                        </th>
                                        <th className="px-4 py-3">Note</th>
                                        <th
                                            className="px-4 py-3"
                                            style={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}
                                        >
                                            Status
                                        </th>
                                        <th className="px-4 py-3">Agreed Amount</th>
                                        <th className="px-4 py-3">Register Date</th>
                                        <th className="px-4 py-3">Checklist</th>
                                        <th className="px-4 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-slate-700">
                                    {filteredInquiries.map((inquiry) => {
                                        const showChecklist = inquiry.statusKey === 'registered';
                                        const showFollowup = !showChecklist && inquiry.statusKey !== 'pending';
                                        const checklistProgress = resolveChecklistProgress(inquiry);
                                        const isChecklistComplete = showChecklist && checklistProgress.percentage === 100;

                                        return (
                                            <tr
                                                key={inquiry.id}
                                                className={`transition-colors ${
                                                    isChecklistComplete
                                                        ? 'bg-emerald-50/70 hover:bg-emerald-100'
                                                        : 'hover:bg-slate-50'
                                                }`}
                                            >
                                                <td
                                                    className="px-4 py-3 font-medium text-slate-900"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'studentId')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'studentId' ? (
                                                        <input
                                                            autoFocus
                                                            type="text"
                                                            value={editingCell.value}
                                                            placeholder="Enter student ID"
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        <div className="flex flex-col gap-1">
                                                            {inquiry.studentId ? (
                                                                <span className="inline-flex w-fit items-center rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-slate-700">
                                                                    {inquiry.studentId}
                                                                </span>
                                                            ) : null}
                                                            <button
                                                                type="button"
                                                                onClick={() => beginEditing(inquiry.id, 'studentId')}
                                                                className={`inline-flex w-fit items-center rounded-full border px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                                                    inquiry.studentId
                                                                        ? 'border-sky-200 bg-sky-50 text-sky-700 focus:ring-sky-300'
                                                                        : 'border-rose-200 bg-rose-50 text-rose-700 focus:ring-rose-300'
                                                                }`}
                                                            >
                                                                {inquiry.studentId ? 'Internal Student' : 'Out Student'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'studentName')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'studentName' ? (
                                                        <input
                                                            autoFocus
                                                            type="text"
                                                            value={editingCell.value}
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        inquiry.studentName ?? '—'
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text text-[11px] font-mono uppercase tracking-wide text-slate-500"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'nic')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'nic' ? (
                                                        <input
                                                            autoFocus
                                                            type="text"
                                                            value={editingCell.value}
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs uppercase tracking-wide text-slate-600 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        inquiry.nic ?? '—'
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'contact')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'contact' ? (
                                                        <input
                                                            autoFocus
                                                            type="text"
                                                            value={editingCell.value}
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        inquiry.contact ?? '—'
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text"
                                                    style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'course')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'course' ? (
                                                        <Select
                                                            autoFocus
                                                            styles={selectStyles}
                                                            menuPortalTarget={selectMenuPortalTarget}
                                                            menuPosition="fixed"
                                                            options={courseSelectOptions}
                                                            value={
                                                                courseSelectOptions.find(
                                                                    (option) => option.value === editingCell.value,
                                                                ) ??
                                                                (inquiry.courseId !== null && inquiry.courseId !== undefined
                                                                    ? courseSelectOptions.find(
                                                                          (option) =>
                                                                              option.value === String(inquiry.courseId),
                                                                      ) ?? null
                                                                    : null)
                                                            }
                                                            onChange={(option) => {
                                                                const selected = option;
                                                                finalizeEdit(true, selected?.value ?? '', {
                                                                    courseOption: selected ?? null,
                                                                    payloadValue: selected ? selected.value : '',
                                                                });
                                                            }}
                                                            onBlur={() => finalizeEdit(false)}
                                                            isClearable
                                                            placeholder="Select course"
                                                            formatOptionLabel={(option) => (
                                                                <div className="flex flex-col">
                                                                    <span className="text-xs font-semibold text-slate-900">
                                                                        {option.shortName ?? option.label}
                                                                    </span>
                                                                    {option.fullName ? (
                                                                        <span className="text-[11px] text-slate-500">
                                                                            {option.fullName}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                            filterOption={(candidate, rawInput) => {
                                                                const search = rawInput.trim().toLowerCase();
                                                                if (!search) {
                                                                    return true;
                                                                }

                                                                const data = candidate.data || {};
                                                                const haystack = [
                                                                    candidate.label,
                                                                    data?.shortName ?? '',
                                                                    data?.fullName ?? '',
                                                                    data?.searchValue ?? '',
                                                                ]
                                                                    .filter(Boolean)
                                                                    .map((value) => value.toLowerCase());

                                                                return haystack.some((value) => value.includes(search));
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="block truncate text-slate-700" title={inquiry.course ?? undefined}>
                                                            {inquiry.course ?? '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text"
                                                    style={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'inquiryType')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'inquiryType' ? (
                                                        enumFieldOptions.inquiryType && enumFieldOptions.inquiryType.length > 0 ? (
                                                            <Select
                                                                autoFocus
                                                                styles={selectStyles}
                                                                menuPortalTarget={selectMenuPortalTarget}
                                                                menuPosition="fixed"
                                                                options={enumFieldOptions.inquiryType}
                                                                value={
                                                                    enumFieldOptions.inquiryType.find(
                                                                        (option) => option.value === editingCell.value,
                                                                    ) ?? null
                                                                }
                                                                onChange={(option) => finalizeEdit(true, option?.value ?? '')}
                                                                onBlur={() => finalizeEdit(true)}
                                                                isClearable
                                                                placeholder="Select type"
                                                            />
                                                        ) : (
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                value={editingCell.value}
                                                                onChange={(event) => updateEditingValue(event.target.value)}
                                                                onBlur={() => finalizeEdit(true)}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        finalizeEdit(true);
                                                                    } else if (event.key === 'Escape') {
                                                                        finalizeEdit(false);
                                                                    }
                                                                }}
                                                                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                            />
                                                        )
                                                    ) : (
                                                        inquiry.inquiryType ?? '—'
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text text-slate-600"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'note')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'note' ? (
                                                        <textarea
                                                            autoFocus
                                                            value={editingCell.value}
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter' && !event.shiftKey) {
                                                                    event.preventDefault();
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            rows={2}
                                                            className="w-full resize-none rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        inquiry.note ?? '—'
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-pointer"
                                                    style={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'statusKey')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'statusKey' ? (
                                                        <Select
                                                            autoFocus
                                                            styles={selectStyles}
                                                            menuPortalTarget={selectMenuPortalTarget}
                                                            menuPosition="fixed"
                                                            options={enumFieldOptions.statusKey ?? STATUS_OPTIONS}
                                                            value={
                                                                (enumFieldOptions.statusKey ?? STATUS_OPTIONS).find(
                                                                    (option) => option.value === editingCell.value,
                                                                ) ?? null
                                                            }
                                                            onChange={(option) => finalizeEdit(true, option?.value ?? inquiry.statusKey)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            placeholder="Select status"
                                                        />
                                                    ) : (
                                                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${getStatusStyles(inquiry.statusKey)}`}>
                                                            {inquiry.statusLabel}
                                                        </span>
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text font-semibold text-slate-900"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'agreedAmount')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'agreedAmount' ? (
                                                        <input
                                                            autoFocus
                                                            type="number"
                                                            step="0.01"
                                                            value={editingCell.value}
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        formatCurrency(inquiry.agreedAmount)
                                                    )}
                                                </td>
                                                <td
                                                    className="px-4 py-3 cursor-text text-slate-600"
                                                    onDoubleClick={() => beginEditing(inquiry.id, 'registerDate')}
                                                >
                                                    {editingCell?.rowId === inquiry.id && editingCell.field === 'registerDate' ? (
                                                        <input
                                                            autoFocus
                                                            type="date"
                                                            value={editingCell.value}
                                                            onChange={(event) => updateEditingValue(event.target.value)}
                                                            onBlur={() => finalizeEdit(true)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') {
                                                                    finalizeEdit(true);
                                                                } else if (event.key === 'Escape') {
                                                                    finalizeEdit(false);
                                                                }
                                                            }}
                                                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-200"
                                                        />
                                                    ) : (
                                                        formatDate(inquiry.registerDate)
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {showChecklist ? (
                                                        <div className="flex flex-col gap-1">
                                                            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-800">
                                                                <span>{checklistProgress.percentage}% complete</span>
                                                                <span className="text-[10px] font-medium text-slate-500">
                                                                    {checklistProgress.completed}/{CHECKLIST_TOTAL}
                                                                </span>
                                                            </div>
                                                            <div className="h-1.5 w-full rounded-full bg-slate-200">
                                                                <div
                                                                    className="h-1.5 rounded-full bg-emerald-500 transition-all"
                                                                    style={{ width: `${checklistProgress.percentage}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-[10px] text-slate-500">
                                                                Registration workflow checklist
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-[11px] text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => openEditModal(inquiry)}
                                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500"
                                                        >
                                                            Edit
                                                        </button>
                                                        {showFollowup && (
                                                            <button
                                                                type="button"
                                                                onClick={() => openFollowUpModal(inquiry)}
                                                                className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] font-semibold text-sky-700 shadow-sm transition hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-300"
                                                            >
                                                                Follow Up
                                                            </button>
                                                        )}
                                                        {showChecklist && (
                                                            <button
                                                                type="button"
                                                                onClick={() => openChecklistModal(inquiry)}
                                                                className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                                            >
                                                                Checklist
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {filteredInquiries.length === 0 && (
                            <div className="p-6 text-center text-sm text-slate-500">
                                No inquiries found. Adjust your search or create a new inquiry to get started.
                            </div>
                        )}
                    </div>
                </section>
            </div>
            )}

            {editModal.isOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="edit-inquiry-title"
                    onClick={closeEditModal}
                >
                    <div
                        className="relative max-h-full w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                            <div>
                                <h3 id="edit-inquiry-title" className="text-lg font-semibold text-slate-900">
                                    {editModal.originalRow ? 'Edit inquiry' : 'Create inquiry'}
                                </h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    {editModal.originalRow
                                        ? 'Keep the inquiry details current so your follow-ups stay coordinated.'
                                        : 'Fill in the details to add a new inquiry. Fields marked with * are required.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeEditModal}
                                className="rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                                <span className="sr-only">Close</span>
                                <svg
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </button>
                        </div>

                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                submitEditModal();
                            }}
                            className="px-6 py-4"
                        >
                            {Object.values(editModal.errors).some((v) => typeof v === 'string' && v.trim().length > 0) && (
                                <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">
                                    Please fix the highlighted fields and try again.
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Student ID
                                    </label>
                                    <input
                                        type="text"
                                        value={editModal.form.studentId}
                                        onChange={(event) => updateEditForm('studentId', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.studentId && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.studentId}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Student name <span className="text-rose-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={editModal.form.studentName}
                                        onChange={(event) => updateEditForm('studentName', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.studentName && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.studentName}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        NIC
                                    </label>
                                    <input
                                        type="text"
                                        value={editModal.form.nic}
                                        onChange={(event) => updateEditForm('nic', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.nic && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.nic}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Primary contact <span className="text-rose-600">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        value={editModal.form.contactNumber1}
                                        onChange={(event) => updateEditForm('contactNumber1', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.contactNumber1 && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.contactNumber1}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Secondary contact
                                    </label>
                                    <input
                                        type="tel"
                                        value={editModal.form.contactNumber2}
                                        onChange={(event) => updateEditForm('contactNumber2', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.contactNumber2 && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.contactNumber2}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Alternate contact
                                    </label>
                                    <input
                                        type="tel"
                                        value={editModal.form.contactNumber3}
                                        onChange={(event) => updateEditForm('contactNumber3', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.contactNumber3 && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.contactNumber3}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Course <span className="text-rose-600">*</span>
                                    </label>
                                    <Select
                                        menuPortalTarget={selectMenuPortalTarget}
                                        menuPosition="fixed"
                                        styles={selectStyles}
                                        options={courseSelectOptions}
                                        value={selectedCourseOption ?? null}
                                        onChange={(option) => updateCourseSelection(option)}
                                        isClearable
                                        placeholder="Select course"
                                        formatOptionLabel={(option) => (
                                            <div className="flex flex-col">
                                                <span className="text-xs font-semibold text-slate-900">
                                                    {option.shortName ?? option.label}
                                                </span>
                                                {option.fullName ? (
                                                    <span className="text-[11px] text-slate-500">{option.fullName}</span>
                                                ) : null}
                                            </div>
                                        )}
                                        filterOption={(candidate, rawInput) => {
                                            const search = rawInput.trim().toLowerCase();
                                            if (!search) {
                                                return true;
                                            }

                                            const data = candidate.data || {};
                                            const haystack = [
                                                candidate.label,
                                                data?.shortName ?? '',
                                                data?.fullName ?? '',
                                                data?.searchValue ?? '',
                                            ]
                                                .filter(Boolean)
                                                .map((value) => value.toLowerCase());

                                            return haystack.some((value) => value.includes(search));
                                        }}
                                    />
                                    {editModal.errors.courseId && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.courseId}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Inquiry type
                                    </label>
                                    <CreatableSelect
                                        menuPortalTarget={selectMenuPortalTarget}
                                        menuPosition="fixed"
                                        styles={selectStyles}
                                        options={inquiryTypeOptions}
                                        value={selectedInquiryTypeOption}
                                        onChange={(option) => updateEditForm('inquiryType', option?.value ?? '')}
                                        onCreateOption={(value) => updateEditForm('inquiryType', value)}
                                        isClearable
                                        placeholder="Select or type inquiry type"
                                    />
                                    {editModal.errors.inquiryType && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.inquiryType}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Status <span className="text-rose-600">*</span>
                                    </label>
                                    <Select
                                        menuPortalTarget={selectMenuPortalTarget}
                                        menuPosition="fixed"
                                        styles={selectStyles}
                                        options={statusOptions}
                                        value={statusOptions.find((option) => option.value === editModal.form.statusKey) ?? null}
                                        onChange={(option) => updateEditForm('statusKey', option?.value ?? '')}
                                        placeholder="Select status"
                                    />
                                    {editModal.errors.statusKey && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.statusKey}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Note
                                    </label>
                                    <textarea
                                        value={editModal.form.note}
                                        onChange={(event) => updateEditForm('note', event.target.value)}
                                        rows={3}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.note && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.note}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Agreed amount (Rs)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editModal.form.agreedAmount}
                                        onChange={(event) => updateEditForm('agreedAmount', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.agreedAmount && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.agreedAmount}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Register date
                                    </label>
                                    <input
                                        type="date"
                                        value={editModal.form.registerDate}
                                        onChange={(event) => updateEditForm('registerDate', event.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                    {editModal.errors.registerDate && (
                                        <p className="mt-1 text-xs text-rose-600">{editModal.errors.registerDate}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="text-xs text-slate-500">
                                    {editModal.originalRow?.studentName ? (
                                        <span>
                                            Editing inquiry for <strong className="font-semibold text-slate-700">{editModal.originalRow.studentName}</strong>.
                                        </span>
                                    ) : (
                                        <span>Review the details and save your changes.</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={closeEditModal}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        disabled={editModal.submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:cursor-not-allowed disabled:bg-slate-400"
                                        disabled={editModal.submitting}
                                    >
                                        {editModal.submitting ? 'Saving...' : 'Save changes'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {followUpModal.isOpen && (
                <div className="fixed inset-0 z-50 flex">
                    {/* backdrop */}
                    <div
                        className="absolute inset-0 bg-slate-900/60"
                        onClick={closeFollowUpModal}
                        aria-hidden="true"
                    />
                    {/* sidebar panel */}
                    <div
                        className="relative ml-0 h-full w-full max-w-md translate-x-0 bg-white shadow-2xl transition-transform duration-300 ease-out"
                        style={{ transform: 'translateX(0)' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="follow-up-title"
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                            <h3 id="follow-up-title" className="text-base font-semibold text-slate-900">Follow Up</h3>
                            <button
                                type="button"
                                onClick={closeFollowUpModal}
                                className="rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z" fill="currentColor" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-4 py-3">
                            <div className="space-y-3">
                                <div className="text-xs text-slate-500">
                                    {followUpModal.row?.studentName ? (
                                        <span>
                                            Plan next steps for <strong className="font-semibold text-slate-700">{followUpModal.row.studentName}</strong>.
                                        </span>
                                    ) : (
                                        <span>Plan your next follow-up.</span>
                                    )}
                                </div>
                                {/* Follow-up form */}
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Add comment</label>
                                    <textarea
                                        value={followUpForm.comment}
                                        onChange={(e) => setFollowUpForm((p) => ({ ...p, comment: e.target.value }))}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                        rows={4}
                                        placeholder="Add follow-up comment..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Next reminder date</label>
                                    <input
                                        type="date"
                                        value={followUpForm.nextReminderDate}
                                        onChange={(e) => setFollowUpForm((p) => ({ ...p, nextReminderDate: e.target.value }))}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Next reminder comment</label>
                                    <textarea
                                        value={followUpForm.nextReminderComment}
                                        onChange={(e) => setFollowUpForm((p) => ({ ...p, nextReminderComment: e.target.value }))}
                                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                        rows={2}
                                        placeholder="Optional note for next reminder..."
                                    />
                                </div>
                                {followUpForm.error && (
                                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{followUpForm.error}</div>
                                )}

                                <div className="flex items-center gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={closeFollowUpModal}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        onClick={submitFollowUp}
                                        className="inline-flex items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:cursor-not-allowed disabled:bg-sky-400"
                                        disabled={followUpForm.submitting}
                                    >
                                        {followUpForm.submitting ? 'Saving...' : 'Save Follow Up'}
                                    </button>
                                </div>

                                {/* History */}
                                <div className="pt-2">
                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Follow-up history</h4>
                                    <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                                        {followUpHistory.length === 0 ? (
                                            <div className="p-3 text-xs text-slate-500">No previous follow-ups yet.</div>
                                        ) : (
                                            <ul className="divide-y divide-slate-100">
                                                {followUpHistory.map((item) => (
                                                    <li key={item.id} className="p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <img
                                                                        src={getImageURL(item.createdByStaff.imagePath)}
                                                                        alt={item.createdByStaff.name ?? 'Staff'}
                                                                        className="h-6 w-6 rounded-full object-cover"
                                                                    />
                                                                <div className="flex flex-col">
                                                                    <span className="text-[11px] font-semibold text-slate-900">
                                                                        {item.createdByStaff?.name ?? item.createdBy ?? '—'}
                                                                    </span>
                                                                    {/* ID intentionally not displayed */}
                                                                </div>
                                                            </div>
                                                            <span className="text-[11px] text-slate-500">
                                                                {formatDisplayDate(item.createdDate || item.commentDate)}
                                                            </span>
                                                        </div>
                                                        {item.comment ? (
                                                            <p className="mt-1 text-xs text-slate-700">{item.comment}</p>
                                                        ) : null}
                                                        {(item.nextReminderDate || item.nextReminderComment) && (
                                                            <div className="mt-2 rounded-md bg-slate-50 p-2 text-[11px] text-slate-600">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="font-semibold text-slate-700">Next follow-up</span>
                                                                    <span className="font-medium text-slate-800">
                                                                        {formatDisplayDate(item.nextReminderDate) ?? '—'}
                                                                    </span>
                                                                </div>
                                                                {item.nextReminderComment ? (
                                                                    <p className="mt-1 text-[11px] text-slate-600">
                                                                        {item.nextReminderComment}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {checklistModal.isOpen && (
                <div className="fixed inset-0 z-50 flex">
                    <div
                        className="absolute inset-0 bg-slate-900/60"
                        onClick={closeChecklistModal}
                        aria-hidden="true"
                    />
                    <div
                        className="relative ml-auto h-full w-full max-w-md translate-x-0 bg-white shadow-2xl transition-transform duration-300 ease-out"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="checklist-title"
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                            <div>
                                <h3 id="checklist-title" className="text-base font-semibold text-slate-900">
                                    Registration checklist
                                </h3>
                                <p className="mt-1 text-xs text-slate-500">
                                    Check off the steps required to complete this registration.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeChecklistModal}
                                className="rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14.348 5.652a.75.75 0 0 0-1.06-1.06L10 7.879 6.712 4.592a.75.75 0 0 0-1.06 1.06L8.94 9l-3.287 3.288a.75.75 0 1 0 1.06 1.06L10 10.121l3.288 3.287a.75.75 0 1 0 1.06-1.06L11.061 9l3.287-3.348Z" fill="currentColor" />
                                </svg>
                            </button>
                        </div>

                        <div className="px-4 py-4">
                            <div className="space-y-3">
                                {checklistModal.row?.studentName ? (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                        <span className="font-semibold text-slate-700">Student:</span> {checklistModal.row.studentName}
                                    </div>
                                ) : null}

                                <div className="space-y-2">
                                    {checklistModal.tasks.map((task) => (
                                        <label
                                            key={task.id}
                                            className="flex cursor-pointer gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition hover:border-sky-200 hover:bg-sky-50"
                                        >
                                            <input
                                                type="checkbox"
                                                className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                                checked={task.completed}
                                                onChange={() => toggleChecklistTask(task.id)}
                                            />
                                            <span>
                                                <span className="font-semibold text-slate-800">{task.label}</span>
                                                {task.description ? (
                                                    <span className="mt-1 block text-xs text-slate-500">{task.description}</span>
                                                ) : null}
                                            </span>
                                        </label>
                                    ))}
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                                    <p className="font-semibold text-slate-700">Need another step?</p>
                                    <p className="mt-1">
                                        Use the follow-up panel to log custom actions or reminders for this inquiry.
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={closeChecklistModal}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SalesLayout>
    );
}
